import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber'
import Joi from 'joi'

import { EntityConstants } from 'constants/EntityConstants'

/**
 * Standard error messages for phone number validation.
 * Use with .messages() on Joi schemas that use phoneValidator or phoneOptionalValidator.
 */
export const phoneValidationMessages = {
  'invalid.phone.number':
    'The telephone number format is invalid. Please input your phone number including the country code: +[countrycode][phone number]',
  'invalid.international.format':
    'The telephone number format is invalid. Please input your phone number including the country code without leading zero: +[countrycode][phone number]',
}

/**
 * Tooltip message for phone number input fields.
 * Use as placeholder or helper text in form components.
 */
export const phoneNumberTooltip =
  'Please input your phone number including the country code: +[countrycode][phone number]'

/**
 * Validates phone numbers using google-libphonenumber library.
 * Returns the value if valid, or triggers Joi error if invalid.
 * Empty/null values are returned as-is.
 */
export const phoneValidator = (value: string, helpers: Joi.CustomHelpers) => {
  const phoneUtil = PhoneNumberUtil.getInstance()
  try {
    const parsedNumber = phoneUtil.parseAndKeepRawInput(value)
    const isValid = phoneUtil.isValidNumber(parsedNumber)

    if (!isValid) {
      return helpers.error('invalid.phone.number')
    }

    const e164 = phoneUtil.format(parsedNumber, PhoneNumberFormat.E164)
    const normalizedInput = value.replace(/\s+/g, '')

    if (normalizedInput.startsWith('+') && normalizedInput !== e164) {
      return helpers.error('invalid.international.format')
    }
  } catch (error) {
    return helpers.error('invalid.phone.number')
  }

  return value
}

/**
 * Optional phone validator that returns empty string for empty/blank values
 * or country codes only (e.g., +44). Otherwise delegates to phoneValidator.
 */
export const phoneOptionalValidator = (value: string, helpers: Joi.CustomHelpers) => {
  if (!value || value.trim() === '' || /^\+\d{1,4}$/.test(value.trim())) {
    return ''
  }

  return phoneValidator(value, helpers)
}

/**
 * Yup-compatible phone validator for required fields.
 * Returns true if valid, false otherwise.
 * Use with yup.test()
 */
export const yupPhoneValidator = (value: string | undefined | null): boolean => {
  if (!value || value.trim() === '') {
    return false
  }

  if (/^\+\d{1,4}$/.test(value.trim())) {
    return false
  }

  const phoneUtil = PhoneNumberUtil.getInstance()
  try {
    const parsedNumber = phoneUtil.parseAndKeepRawInput(value)
    const isValid = phoneUtil.isValidNumber(parsedNumber)

    if (!isValid) {
      return false
    }

    const e164 = phoneUtil.format(parsedNumber, PhoneNumberFormat.E164)
    const normalizedInput = value.replace(/\s+/g, '')

    if (normalizedInput.startsWith('+') && normalizedInput !== e164) {
      return false
    }

    return true
  } catch (error) {
    return false
  }
}

/**
 * Yup-compatible phone validator for optional fields.
 * Returns true if valid or empty, false if invalid.
 * Use with yup.test()
 */
export const yupPhoneOptionalValidator = (value: string | undefined | null): boolean => {
  if (!value || value.trim() === '' || /^\+\d{1,4}$/.test(value.trim())) {
    return true
  }
  return yupPhoneValidator(value)
}

/**
 * Reusable Joi schema for required phone number fields.
 * Includes validation, max length, error messages, and default label.
 * Can override label with .label() if needed.
 *
 * Example:
 * phoneNumberSchema.label('Contact Number')
 */
export const phoneNumberSchema = Joi.string()
  .required()
  .custom(phoneValidator)
  .max(EntityConstants.MAX_TELEPHONE_NUMBER_LENGTH)
  .messages(phoneValidationMessages)
  .label('Telephone Number')

/**
 * Reusable Joi schema for optional phone number fields.
 * Allows null and empty strings, includes validation, max length, and error messages.
 * Can override label with .label() if needed.
 *
 * Example:
 * phoneNumberOptionalSchema.label('Alternative Telephone Number')
 */
export const phoneNumberOptionalSchema = Joi.string()
  .allow(null, '')
  .optional()
  .custom(phoneOptionalValidator)
  .max(EntityConstants.MAX_TELEPHONE_NUMBER_LENGTH)
  .messages(phoneValidationMessages)
  .label('Telephone Number')
